import { Component, OnInit } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { Logger, PreviewMode, UtilityService } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { AzureAssetsSearchService, SearchAsset } from 'src/app/modules/standard/v1';
import { SelectionModel } from '@angular/cdk/collections';
import * as dayjs from 'dayjs';
import { ActivatedRoute, Router } from '@angular/router';

const log = new Logger('AssetListTableComponent');

@Component({
  selector: 'app-asset-list-table',
  templateUrl: './asset-list-table.component.html',
  styleUrls: ['./asset-list-table.component.scss']
})

export class AssetListTableComponent implements OnInit {
  viewModel$ = combineLatest([
    this.azureAssetsSearchService.assets$,
    this.azureAssetsSearchService.loading$,
    this.azureAssetsSearchService.totalAssetsCount$,
    this.azureAssetsSearchService.searchStateParams$,
  ]).pipe(
    map(([assets, isLoading, totalRecords, searchStateParams]) => {
      this.assetsList = assets;
      assets.forEach(asset => asset.dateAdded = dayjs(asset.dateAdded).format('MM/DD/YYYY'));
      return { assets, isLoading, totalRecords, searchStateParams }
    }),
  );

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'Image', 'AssetName', 'FileName', 'FileType', 'Group', 'DateAdded', 'Actions'];
  assetsList: SearchAsset[] = [];
  selection = new SelectionModel<SearchAsset>(true, []);
  PreviewMode = PreviewMode;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private mediaObserver: MediaObserver,
    public utilityService: UtilityService,
    private azureAssetsSearchService: AzureAssetsSearchService
  ) { }

  ngOnInit(): void {
    log.debug('init');

    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'Image', 'AssetName', 'Actions'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'Image', 'AssetName', 'Group', 'DateAdded', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'Image', 'AssetName', 'FileName', 'FileType', 'Group', 'DateAdded', 'Actions'];
        }
      });
  }

  ngOnDestroy(): void {
    this.flexMediaWatcher.unsubscribe();
  }

  onSort(sortState: Sort): void {
    let orderby = '';
    switch (sortState.active) {
      case 'AssetName':
        orderby = 'assetName';
        break;
      case 'DateAdded':
        orderby = 'dateAddedEPOCH';
        break;
      case 'Group':
        orderby = 'assetGroupName';
        break;
      case 'FileType':
        orderby = 'fileType';
        break;
      default:
        orderby = 'dateAddedEPOCH';
        break;
    }

    this.clearSelection();
    this.azureAssetsSearchService.sort(orderby, sortState.direction as 'asc' | 'desc');
    this.router.navigate([], { relativeTo: this.route, queryParams: { filter: true }, queryParamsHandling: 'merge' });
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.router.navigate([], { relativeTo: this.route, queryParams: { filter: true }, queryParamsHandling: 'merge' });
    this.azureAssetsSearchService.pageChange(pageEvent.pageIndex, pageEvent.pageSize);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.assetsList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.assetsList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  onImageError(event: Event): void {
    const element = event.target as HTMLImageElement;
    element.src = './assets/images/default-image.png'; // Path to your default image
  }

  onEdit(asset: SearchAsset, index: number) {
    this.azureAssetsSearchService.openAsset(asset, index);
    this.router.navigate([asset.assetId], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }
}
